/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
/* eslint-disable react-hooks/rules-of-hooks */
import { CloseOutlined } from '@ant-design/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { Form, Input, Modal, Select, Switch } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { useUser } from '@contexts'
import { useGetTelePhonePrefixes } from '@hooks'
import { Driver, TelephonePrefix, Trailer, Truck, UpdateDriverData } from '@types'

import { StyledForm, StyledRow } from '../../../../../components/StyledComponents'
import checkPrefix from '../../../../../utils/check-preffix'

type Props = {
  open: boolean
  onClose: () => void
  onEdit: (data: UpdateDriverData) => void
  driver: Driver
}

const EditDriverModal: React.FC<Props> = ({ open, onClose, onEdit, driver }) => {
  const dataSwitch = useMemo(() => driver.driverByCarrierData[0].disableAt === null, [driver])

  const { t } = useTranslation()

  const { telephonePrefixes } = useGetTelePhonePrefixes()

  const [phoneValue, setPhoneValue] = useState<string>('')
  const [disableAtSwitch, setDisableAtSwitch] = useState<boolean>(dataSwitch)
  const { state: user } = useUser()
  const carrierTrucks = useMemo(() => user?.userDetail?.carrier?.trucks, [user])
  const carrierTrailers = useMemo(() => user?.userDetail?.carrier?.trailers, [user])
  const { Option } = Select
  const carrierDrivers = useMemo(() => user?.userDetail?.carrier?.drivers, [user])

  const [selectedPrefix, setSelectedPrefix] = useState<string>()

  const selectedTelephonePrefix = useMemo(
    () =>
      telephonePrefixes?.find(
        (oneTelephonePrefix: TelephonePrefix) => oneTelephonePrefix.prefix === selectedPrefix,
      ),
    [telephonePrefixes, selectedPrefix],
  )

  const minTelephoneLength = useMemo(
    () => selectedTelephonePrefix?.min ?? 0,
    [selectedTelephonePrefix],
  )
  const maxTelephoneLength = useMemo(
    () => selectedTelephonePrefix?.min ?? 100,
    [selectedTelephonePrefix],
  )

  const validationSchema = yup.object().shape({
    phone: yup
      .string()
      .required(t('globals.obligatory_field'))
      .min(
        minTelephoneLength,
        t('tpl_router.tpl_dashboard.driver_validations.phone_required_single', {
          length: minTelephoneLength,
        }),
      )
      .max(
        maxTelephoneLength,
        t('tpl_router.tpl_dashboard.driver_validations.phone_required_single', {
          length: maxTelephoneLength,
        }),
      )
      .test(
        'no-only-spaces',
        t('tpl_router.tpl_dashboard.driver_validations.phone_number_not_just_spaces'),
        value => {
          if (value?.trim() === '') {
            throw new yup.ValidationError(
              t('tpl_router.tpl_dashboard.driver_validations.phone_number_not_just_spaces'),
              value,
              'phone',
            )
          }
          return true
        },
      )
      .test(
        'unique-phone',
        t('tpl_router.tpl_dashboard.driver_validations.drivers_phone_already_exists'),
        value =>
          !carrierDrivers?.some(
            carrierDriver => carrierDriver.phone === value && carrierDriver.id !== driver.id,
          ),
      ),
  })
  const { reset, handleSubmit, register, setValue, formState, setError } = useForm<{
    firstName: string
    lastName: string
    stateIdNumber: string
    phone: string
    preferredTruck: number
    preferredTrailer: number
    disableAt: boolean
  }>({
    resolver: yupResolver(validationSchema),
  })

  useEffect(() => {
    if (open && driver) {
      // Resetea el formulario con los valores del driver
      reset({
        firstName: driver.firstName,
        lastName: driver.lastName,
        stateIdNumber: driver.stateIdNumber,
        phone: driver.phone,
        preferredTruck: driver.preferredTrucks?.[0]?.id,
        preferredTrailer: driver.preferredTrailers?.[0]?.id,
        disableAt: driver.driverByCarrierData[0].disableAt === null,
      })
    }
  }, [open, driver, reset])

  const onSubmit = useCallback(
    async (updateData: UpdateDriverData) => {
      updateData.disabled = !disableAtSwitch

      try {
        await validationSchema.validate(updateData)

        onEdit(updateData)
        reset()
      } catch (err: any) {
        const fieldName = err?.path as 'phone' | 'preferredTruck' | 'disableAt' | 'preferredTrailer'
        setError(`${fieldName}`, {
          type: 'manual',
          message: `${err?.message}`,
        })
      }
    },
    // eslint-disable-next-line
    [phoneValue, disableAtSwitch, selectedPrefix],
  )

  useEffect(() => {
    if (driver.phone) {
      const { phone, prefix } = checkPrefix(
        driver.phone,
        telephonePrefixes.map(item => item.prefix),
      )
      setSelectedPrefix(prefix)
      setPhoneValue(phone)
    }
  }, [driver])

  useEffect(() => {
    setValue('phone', `${selectedPrefix}${phoneValue}`)
  }, [phoneValue, selectedPrefix])

  return (
    <Modal
      open={open}
      keyboard
      maskClosable
      onCancel={() => {
        reset()
        onClose()
      }}
      title={t('tpl_router.tpl_dashboard.edit_driver_modal.title')}
      closeIcon={<CloseOutlined />}
      okText={t('tpl_router.tpl_dashboard.edit_driver_modal.button_add')}
      cancelText={t('tpl_router.tpl_dashboard.edit_driver_modal.button_cancel')}
      onOk={handleSubmit(
        onSubmit as unknown as SubmitHandler<{
          firstName: string
          lastName: string
          stateIdNumber: string
          phone: string
          preferredTruck: number
          preferredTrailer: number
          disableAt: boolean
        }>,
      )}
      destroyOnClose
    >
      <StyledForm
        initialValues={{
          ...driver,
          preferredTruck: driver.preferredTrucks?.[0]?.id,
          preferredTrailer: driver.preferredTrailers?.[0]?.id,
          disabledAt: driver.driverByCarrierData[0].disableAt === undefined ? true : dataSwitch,
        }}
      >
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_driver_modal.firstName')}
            name="firstName"
            hasFeedback
            validateStatus={formState.errors.firstName ? 'error' : ''}
            help={formState.errors.firstName?.message}
          >
            <Input
              {...register('firstName')}
              onChange={e => {
                setValue('firstName', e.target.value)
              }}
              disabled
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_driver_modal.lastName')}
            name="lastName"
            hasFeedback
            validateStatus={formState.errors.lastName ? 'error' : ''}
            help={formState.errors.lastName?.message}
          >
            <Input
              {...register('lastName')}
              onChange={e => {
                setValue('lastName', e.target.value)
              }}
              disabled
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_driver_modal.stateIdNumber')}
            name="stateIdNumber"
            hasFeedback
            validateStatus={formState.errors.stateIdNumber ? 'error' : ''}
            help={formState.errors.stateIdNumber?.message}
          >
            <Input
              {...register('stateIdNumber')}
              onChange={e => {
                setValue('stateIdNumber', e.target.value)
              }}
              disabled
            />
          </StyledForm.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_driver_modal.phone')}
            hasFeedback
            validateStatus={formState.errors.phone ? 'error' : ''}
            help={formState.errors.phone?.message}
          >
            <Input
              addonBefore={
                <Select value={selectedPrefix} onChange={setSelectedPrefix}>
                  {telephonePrefixes?.map(prefix => (
                    <Option key={prefix.prefix} value={prefix.prefix}>
                      {`+${prefix.prefix}`}
                    </Option>
                  ))}
                </Select>
              }
              {...register('phone')}
              value={phoneValue}
              onChange={e => {
                setPhoneValue(e.target.value)
              }}
              maxLength={15}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item
            label={t('tpl_router.tpl_dashboard.edit_driver_modal.preferredTruck')}
            name="preferredTruck"
            hasFeedback
            validateStatus={formState.errors.preferredTruck ? 'error' : ''}
            help={formState.errors.preferredTruck?.message}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              filterOption={(input, option) => {
                const truck = carrierTrucks?.find(
                  (tr: { id: string | number }) => tr.id === option?.value,
                )
                if (truck) {
                  const labelTruck = `${truck.licensePlate} ${truck.description}${
                    truck.equipmentType ? ` ${truck.equipmentType.name}` : ''
                  }`.toLowerCase()
                  return labelTruck.includes(input.toLowerCase())
                }
                return false
              }}
              options={
                carrierTrucks?.map((truck: Truck) => {
                  return {
                    label: `${truck.licensePlate} - ${truck.description}${
                      truck.equipmentType ? ` - ${truck.equipmentType.name}` : ''
                    }`,
                    value: truck.id,
                  }
                }) || []
              }
              onChange={value => {
                setValue('preferredTruck', value)
              }}
            />
          </Form.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item
            label={t('tpl_router.tpl_dashboard.edit_driver_modal.preferredTrailer')}
            name="preferredTrailer"
            hasFeedback
            validateStatus={formState.errors.preferredTrailer ? 'error' : ''}
            help={formState.errors.preferredTrailer?.message}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              filterOption={(input, option) => {
                const trailer = carrierTrailers?.find(
                  (tr: { id: string | number }) => tr.id === option?.value,
                )
                if (trailer) {
                  const labelTrailer = `${trailer.licensePlate} ${trailer.description}${
                    trailer.equipmentType ? ` ${trailer.equipmentType.name}` : ''
                  }`.toLowerCase()
                  return labelTrailer.includes(input.toLowerCase())
                }
                return false
              }}
              options={
                carrierTrailers?.map((trailer: Trailer) => {
                  return {
                    label: `${trailer.licensePlate} - ${trailer.description}${
                      trailer.equipmentType ? ` - ${trailer.equipmentType.name}` : ''
                    }`,
                    value: trailer.id,
                  }
                }) || []
              }
              onChange={value => {
                setValue('preferredTrailer', value)
              }}
            />
          </Form.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledRow className="carrier-form-row">
            <Form.Item label={t('globals.generics.statusTitle')} name="disableAt">
              <Switch
                checked={disableAtSwitch}
                onChange={checked => {
                  setDisableAtSwitch(checked)
                }}
                checkedChildren={t('globals.generics.check_enabled')}
                unCheckedChildren={t('globals.generics.check_disabled')}
              />
            </Form.Item>
          </StyledRow>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { EditDriverModal }
